'use client';

import { Alert, Box, Stack, Typography } from '@mui/material';

import { CopyToClipboardButton } from '@/components/buttons/copyToClipboardButton';
import { StepContainer } from '@/components/wizard/StepContainer';
import type { StepProps } from '@/components/wizard/StepProps';
import { useDAL } from '@/contexts/useDAL';

import type { RdsCdcWizardState } from '../rdsCdcWizardState';

export const SqlCommandsStep = (props: StepProps<RdsCdcWizardState>) => {
  const { wizardState: state, currentStep, setNextStep } = props;
  const dal = useDAL();
  const { data } = dal.inventory.privateLink.useOnboardRdsCdc(state.resourceId);

  const sqlInstructions = data?.sqlInstructions;
  const setupScript = sqlInstructions?.setupScript ?? '';

  return (
    <StepContainer
      stepperIndex={1}
      canGoNext={() => !!sqlInstructions}
      nextButtonText='Next'
      onNextClick={async () => {
        setNextStep(currentStep.next?.[0]);
      }}
      onBackClick={props.back}
      sx={{ overflow: 'auto', paddingX: '0px', paddingY: 'min(60px, 4vw)' }}
    >
      <Box paddingX='min(100px, 6vw)'>
        <Stack gap='24px' maxWidth='720px'>
          <Stack gap='8px'>
            <Typography variant='h6'>SQL Commands</Typography>
            <Typography variant='body2' color='text.secondary'>
              Run the following SQL commands against your RDS instance to set up
              the replication user and publication.
            </Typography>
          </Stack>

          {!sqlInstructions && (
            <Alert severity='warning'>
              SQL setup data not available. Please go back and complete the
              previous step first.
            </Alert>
          )}

          {setupScript && (
            <Stack
              direction='row'
              alignItems='flex-start'
              gap='8px'
              sx={{
                bgcolor: 'action.hover',
                borderRadius: '8px',
                p: '12px',
                overflowX: 'auto',
              }}
            >
              <Typography
                variant='body1'
                component='code'
                sx={{
                  flex: 1,
                  whiteSpace: 'pre-wrap',
                  wordBreak: 'break-all',
                  fontFamily: 'monospace',
                }}
              >
                {setupScript}
              </Typography>
              <CopyToClipboardButton textToCopy={setupScript} />
            </Stack>
          )}
        </Stack>
      </Box>
    </StepContainer>
  );
};
